import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { isDaniyalUserId } from "../../utils/isDaniyalUser";
import DeleteSubTaskModal from "../DeleteSubTaskModal";
import SubtaskBlockerModal from "../SubtaskBlockerModal";
import TablePagination from "./TablePagination";
import TruncatedTooltipCell from "./TruncatedTooltipCell";
import {
  parseCommitHashesForDisplay,
  parseVerificationEndpoints,
} from "../../utils/subtaskPayload";

const SUBJOB_ACTION_ICONS = {
  view: "mdi:eye-outline",
  logHours: "mdi:clipboard-text-clock-outline",
  markComplete: "mdi:check-decagram-outline",
  markIncomplete: "mdi:toggle-switch-off-outline",
  edit: "mdi:pencil-outline",
  blocker: "mdi:hand-back-right-off-outline",
  delete: "mdi:delete-outline",
} as const;

function shortRepoLabel(url: string): string {
  const stripped = url.replace(/^https?:\/\//i, "");
  return stripped.length > 56 ? `${stripped.slice(0, 56)}…` : stripped || url;
}

function formatEstTime(hrs: unknown, mins: unknown): string {
  const h = Number(hrs);
  const m = Number(mins);
  const safeH = Number.isFinite(h) ? Math.max(0, Math.floor(h)) : 0;
  const safeM = Number.isFinite(m) ? Math.max(0, Math.floor(m)) : 0;
  if (safeH === 0 && safeM === 0) return "—";
  if (safeH === 0) return `${safeM} min`;
  if (safeM === 0) return `${safeH} h`;
  return `${safeH} h ${safeM} min`;
}

export type SubJobsProps = {
  user: { user_id?: string } | null | undefined;
  DANIYAL: string;
  subtasks: any[];
  setTaskId: (task: any) => void;
  updateSubtaskStatus: (e: any, subtask: any) => Promise<void>;
  handleSubTaskEditClick: (subtask: any) => void;
  selectedSubTask: any;
  setSelectedSubTask: React.Dispatch<React.SetStateAction<any>>;
  setSubTasks: React.Dispatch<React.SetStateAction<any>>;
  setTasks1: React.Dispatch<React.SetStateAction<any>>;
  setTasks2: React.Dispatch<React.SetStateAction<any>>;
  currentPage2: number;
  totalPages2: number;
  goToPage2: (page: number) => void;
  goToPreviousPage2: () => void;
  goToNextPage2: () => void;
};

const SubJobs = ({
  user,
  DANIYAL,
  subtasks,
  setTaskId,
  updateSubtaskStatus,
  handleSubTaskEditClick,
  selectedSubTask,
  setSelectedSubTask,
  setSubTasks,
  setTasks1,
  setTasks2,
  currentPage2,
  totalPages2,
  goToPage2,
  goToPreviousPage2,
  goToNextPage2,
}: SubJobsProps) => {
  const [viewSubtask, setViewSubtask] = useState<any | null>(null);
  const [selectedBlockerSubtask, setSelectedBlockerSubtask] = useState<any | null>(
    null,
  );

  if (!isDaniyalUserId(user?.user_id, DANIYAL)) {
    return null;
  }

  return (
    <>
      <h2>Sub Jobs</h2>

      <div className="table-responsive employee-subjobs-table-responsive">
        <table className="table caption-top employee-subjobs-table">
          <thead className="table-light">
            <tr>
              <th scope="col" className="text-center">
                Job ID
              </th>
              <th scope="col" className="text-center">
                SubJob Name
              </th>
              <th scope="col" className="text-center">
                Created At
              </th>
              <th scope="col" className="text-center">
                Est. (Hours)
              </th>
              <th scope="col" className="text-center">
                Est. (Mins)
              </th>
              <th scope="col" className="text-center">
                Status
              </th>
              <th scope="col" className="text-center text-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {subtasks && subtasks.length > 0 ? (
              subtasks.map((subtask: any, index: number) => (
                <tr key={index}>
                  <td className="text-center align-middle subjobs-id-cell">
                    <TruncatedTooltipCell text={subtask.projectId} />
                  </td>
                  <td className="align-middle subjobs-name-cell">
                    <TruncatedTooltipCell text={subtask.name} />
                  </td>
                  <td className="text-center align-middle text-nowrap">
                    {subtask.createdDate}
                  </td>
                  <td className="text-center align-middle">
                    {subtask.estimatedtimeHrs}
                  </td>
                  <td className="text-center align-middle">
                    {subtask.estimatedtimeMin}
                  </td>
                  <td className="text-center align-middle">
                    <span
                      className={`badge ${
                        subtask.status ? "text-bg-success" : "text-bg-primary"
                      }`}
                    >
                      {subtask.status ? "Completed" : "In Progress"}
                    </span>
                  </td>
                  <td className="subjobs-actions-cell text-center align-middle">
                    <div
                      className="subjobs-actions-toolbar"
                      role="group"
                      aria-label="Sub job actions"
                    >
                      <button
                        type="button"
                        className="subjobs-action-btn"
                        title="View full sub job details"
                        aria-label="View sub job details"
                        onClick={() => setViewSubtask(subtask)}
                      >
                        <Icon
                          icon={SUBJOB_ACTION_ICONS.view}
                          width={22}
                          height={22}
                          aria-hidden
                        />
                      </button>
                      {!subtask?.status ? (
                        <button
                          type="button"
                          className="subjobs-action-btn"
                          title="Open log hours for this sub job"
                          aria-label="Log hours on this sub job"
                          data-bs-target="#exampleModalTogglee"
                          data-bs-toggle="modal"
                          onClick={() => setTaskId(subtask)}
                        >
                          <Icon
                            icon={SUBJOB_ACTION_ICONS.logHours}
                            width={22}
                            height={22}
                            aria-hidden
                          />
                        </button>
                      ) : null}
                      {!subtask?.status ? (
                        <button
                          type="button"
                          className="subjobs-action-btn subjobs-action-btn--success"
                          title="Mark this sub job as completed"
                          aria-label="Mark sub job as completed"
                          onClick={(e) => void updateSubtaskStatus(e, subtask)}
                        >
                          <Icon
                            icon={SUBJOB_ACTION_ICONS.markComplete}
                            width={22}
                            height={22}
                            aria-hidden
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="subjobs-action-btn subjobs-action-btn--muted"
                          title="Mark this sub job as incomplete (in progress)"
                          aria-label="Mark sub job as incomplete"
                          onClick={(e) => void updateSubtaskStatus(e, subtask)}
                        >
                          <Icon
                            icon={SUBJOB_ACTION_ICONS.markIncomplete}
                            width={22}
                            height={22}
                            aria-hidden
                          />
                        </button>
                      )}
                      <button
                        type="button"
                        className={`subjobs-action-btn${
                          subtask.blocker ? " subjobs-action-btn--warning" : ""
                        }`}
                        title={
                          subtask.blocker
                            ? `Blocker: ${subtask.blocker}`
                            : "Set or update blocker"
                        }
                        aria-label="Set sub job blocker"
                        data-bs-toggle="modal"
                        data-bs-target="#subtaskBlockerModal"
                        onClick={() => setSelectedBlockerSubtask(subtask)}
                      >
                        <Icon
                          icon={SUBJOB_ACTION_ICONS.blocker}
                          width={22}
                          height={22}
                          aria-hidden
                        />
                      </button>
                      <button
                        type="button"
                        className="subjobs-action-btn"
                        title="Edit this sub job"
                        aria-label="Edit sub job"
                        data-bs-toggle="modal"
                        data-bs-target="#editSubtaskModal"
                        onClick={() => handleSubTaskEditClick(subtask)}
                      >
                        <Icon
                          icon={SUBJOB_ACTION_ICONS.edit}
                          width={22}
                          height={22}
                          aria-hidden
                        />
                      </button>
                      {subtask.userId === user?.user_id && (
                        <button
                          type="button"
                          className="subjobs-action-btn subjobs-action-btn--danger"
                          title="Delete this sub job"
                          aria-label="Delete sub job"
                          data-bs-toggle="modal"
                          data-bs-target="#deleteSubTaskModal"
                          onClick={() => setSelectedSubTask(subtask)}
                        >
                          <Icon
                            icon={SUBJOB_ACTION_ICONS.delete}
                            width={22}
                            height={22}
                            aria-hidden
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No Jobs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DeleteSubTaskModal
        subtask={selectedSubTask}
        setSubTasks={setSubTasks}
        setSelectedSubTask={setSelectedSubTask}
        setTasks1={setTasks1}
        setTasks2={setTasks2}
      />

      <SubtaskBlockerModal
        subtask={selectedBlockerSubtask}
        setSelectedBlockerSubtask={setSelectedBlockerSubtask}
        setSubTasks={setSubTasks}
      />

      <TablePagination
        currentPage={currentPage2}
        totalPages={totalPages2}
        onPageChange={goToPage2}
        onPrevious={goToPreviousPage2}
        onNext={goToNextPage2}
        className="employee-table-pagination"
        ariaLabel="Sub jobs table pagination"
      />

      {viewSubtask && (() => {
        const verificationLinks = parseVerificationEndpoints(
          viewSubtask.verificationEndpoints ??
            viewSubtask.verificationEndpoint,
        );
        const commitHashGroups = parseCommitHashesForDisplay(
          viewSubtask.commitHashes,
        );
        return (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1080 }}
            aria-hidden
            onClick={() => setViewSubtask(null)}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employeeSubjobDetailTitle"
            style={{ zIndex: 1085 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow employee-log-detail-card">
                <div className="employee-log-detail-header text-white p-4 pb-3 position-relative">
                  <h2 className="h5 mb-1 fw-semibold" id="employeeSubjobDetailTitle">
                    Sub job details
                  </h2>
                  <p className="mb-0 opacity-75 small">
                    {String(viewSubtask?.name ?? "—")}
                  </p>
                  <button
                    type="button"
                    className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                    aria-label="Close"
                    onClick={() => setViewSubtask(null)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Job ID</div>
                        <div className="employee-log-detail-value">
                          {String(viewSubtask?.projectId ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Sub job ID</div>
                        <div className="employee-log-detail-value">
                          {String(viewSubtask?.subtaskId ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Sub job name</div>
                        <div className="employee-log-detail-value">
                          {String(viewSubtask?.name ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Description</div>
                        <div className="employee-log-detail-value employee-log-detail-notes">
                          {String(viewSubtask?.taskDescriptions ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Created at</div>
                        <div className="employee-log-detail-value">
                          {String(viewSubtask?.createdDate ?? "—")}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Estimated time</div>
                        <div className="employee-log-detail-value">
                          {formatEstTime(
                            viewSubtask?.estimatedtimeHrs,
                            viewSubtask?.estimatedtimeMin,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="employee-log-detail-item">
                        <div className="employee-log-detail-label">Status</div>
                        <div className="employee-log-detail-value">
                          {viewSubtask?.status ? "Completed" : "In progress"}
                        </div>
                      </div>
                    </div>
                    {viewSubtask?.blocker ? (
                      <div className="col-12">
                        <div className="employee-log-detail-item">
                          <div className="employee-log-detail-label">Blocker</div>
                          <div className="employee-log-detail-value employee-log-detail-notes">
                            {String(viewSubtask.blocker)}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {verificationLinks.length > 0 ? (
                      <div className="col-12">
                        <div className="employee-log-detail-item">
                          <div className="employee-log-detail-label">
                            Verification links
                          </div>
                          <ul className="list-unstyled mb-0 employee-log-detail-value">
                            {verificationLinks.map((url) => (
                              <li key={url} className="mb-1">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-break"
                                >
                                  {url}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : null}
                    {commitHashGroups.length > 0 ? (
                      <div className="col-12">
                        <div className="employee-log-detail-item">
                          <div className="employee-log-detail-label">
                            Commit hashes
                          </div>
                          <div className="employee-log-detail-value">
                            {commitHashGroups.map((group) => (
                              <div
                                key={group.repoUrl}
                                className="subjob-view-commit-repo mb-3"
                              >
                                <span
                                  className="small text-muted text-truncate d-block mb-1"
                                  title={group.repoUrl}
                                >
                                  {shortRepoLabel(group.repoUrl)}
                                </span>
                                <div className="d-flex flex-wrap gap-1">
                                  {group.hashes.map((hash) => (
                                    <code
                                      key={`${group.repoUrl}-${hash}`}
                                      className="subjob-view-commit-hash"
                                    >
                                      {hash}
                                    </code>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div
                  className={`modal-footer gap-2 flex-wrap ${
                    !viewSubtask?.status ? "justify-content-between" : "justify-content-end"
                  }`}
                >
                  {!viewSubtask?.status ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      title="Open log hours for this sub job"
                      data-bs-target="#exampleModalTogglee"
                      data-bs-toggle="modal"
                      onClick={() => {
                        setTaskId(viewSubtask);
                        setViewSubtask(null);
                      }}
                    >
                      Log hours
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setViewSubtask(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
        );
      })()}
    </>
  );
};

export default SubJobs;
